import { useState } from 'react';
import { useBillables, useSetClientBillable, useUpdateEntity } from '@/hooks/useReference';
import { useBillingNouns } from '@/hooks/useSession';
import type { BillableParty, Client } from '@/types';
import { Sheet, SheetBack } from '@m/ui/Sheet';
import { BarButton, Segmented } from '@m/ui/Controls';
import { Avatar, Group, Row } from '@m/ui/List';
import { PickerList } from '@m/ui/Picker';
import { FieldRow } from './forms';
import { BillingPartySheet } from './BillingPartySheet';

/** Edit a client: name, rate, billing party (picked in a pushed panel), status. */
export function ClientEditSheet({ open, onClose, client }: { open: boolean; onClose: () => void; client: Client }) {
  const nouns = useBillingNouns();
  const billables = useBillables().data ?? [];
  const update = useUpdateEntity('client');
  const setBillable = useSetClientBillable();

  const initial = {
    name: client.name,
    rate: client.hourlyRate != null ? String(client.hourlyRate) : '',
    billableId: client.billable?.id ?? null,
    status: client.status === 'inactive' ? 'inactive' : 'active'
  } as const;
  const [name, setName] = useState<string>(initial.name);
  const [rate, setRate] = useState<string>(initial.rate);
  const [billableId, setBillableId] = useState<string | null>(initial.billableId);
  const [status, setStatus] = useState<'active' | 'inactive'>(initial.status);
  const [picking, setPicking] = useState(false);
  const [creating, setCreating] = useState<string | null>(null);

  const dirty =
    name !== initial.name || rate !== initial.rate || billableId !== initial.billableId || status !== initial.status;
  const rateValue = rate.trim() === '' ? null : Number(rate);
  const valid = name.trim() !== '' && (rateValue === null || (Number.isFinite(rateValue) && rateValue >= 0));

  const save = () => {
    if (!valid) return;
    const patch: Record<string, unknown> = {};
    if (name.trim() !== client.name) patch.name = name.trim();
    if (rateValue !== client.hourlyRate) patch.hourlyRate = rateValue;
    if (status !== initial.status) patch.status = status;
    if (Object.keys(patch).length) update.mutate({ id: client.id, patch });
    if (billableId !== initial.billableId) setBillable.mutate({ clientId: client.id, billableId });
    onClose();
  };

  const party = billables.find((b) => b.id === billableId);

  return (
    <>
      <Sheet
        open={open}
        onClose={onClose}
        full
        dirty={dirty}
        title={picking ? nouns.payer : `Edit ${nouns.clientLower}`}
        leading={picking ? <SheetBack label="Edit" onClick={() => setPicking(false)} /> : <BarButton onClick={onClose}>Cancel</BarButton>}
        trailing={
          picking ? undefined : (
            <BarButton className="font-semibold" disabled={!dirty || !valid} onClick={save}>
              Save
            </BarButton>
          )
        }
      >
        {picking ? (
          <PickerList<BillableParty>
            items={billables}
            value={billableId}
            noun={nouns.payer.toLowerCase()}
            onSelect={(b) => {
              setBillableId(b.id);
              setPicking(false);
            }}
            onCreate={async (n) => {
              setCreating(n);
              return undefined;
            }}
            leading={(b) => <Avatar id={b.id} name={b.name} size={26} />}
          />
        ) : (
          <div className="flex flex-col gap-6">
            <Group>
              <FieldRow label="Name" value={name} onChange={(e) => setName(e.target.value)} />
              <FieldRow label="Hourly rate" inputMode="decimal" placeholder="Team default" value={rate} onChange={(e) => setRate(e.target.value)} />
            </Group>
            <Group footer={`Who the invoice is addressed and emailed to. Without one the ${nouns.clientLower} bills itself.`}>
              <Row title={nouns.payer} detail={party?.name ?? 'Bills itself'} onClick={() => setPicking(true)} chevron />
              {billableId && <Row title={`Remove ${nouns.payer.toLowerCase()}`} destructive onClick={() => setBillableId(null)} />}
            </Group>
            <Group header="Status" footer={`Inactive ${nouns.clients.toLowerCase()} stay on old invoices but leave the time pickers.`}>
              <div className="p-3">
                <Segmented
                  value={status}
                  onChange={setStatus}
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' }
                  ]}
                />
              </div>
            </Group>
          </div>
        )}
      </Sheet>
      <BillingPartySheet
        key={creating ?? 'none'}
        open={creating !== null}
        party={null}
        initialName={creating ?? ''}
        onClose={() => setCreating(null)}
        onCreated={(p) => {
          setBillableId(p.id);
          setPicking(false);
        }}
      />
    </>
  );
}
